<script>
    import {
        Chart as ChartJS,
        CategoryScale,
        LinearScale,
        BarElement,
        Title,
        Tooltip,
        Legend,
        Colors
    } from 'chart.js';
    import { Bar } from 'vue-chartjs';

    import ChartDataLabels from 'chartjs-plugin-datalabels';

    ChartJS.register(
        CategoryScale,
        LinearScale,
        BarElement,
        Title,
        Tooltip,
        Legend,
        Colors,
        ChartDataLabels,
    );

    // Import general mixin for shared functionality.
    import mixin from '../../mixin';

    export default {
        mixins: [mixin],
        components: {
            Bar,
        },
        props: {
            data: Object,
            periods: Array,
            mostRecentYear: Number,
            secondRecentYear: Number,
        },
        data() {
            return {
                facetAlias: 'ingangsjaarstatus',
                facetAliasStatus: 'lopend_afgesloten',
                currentLabel: 'lopend',
                closedLabel: 'afgesloten',
                options: {
                    locale: 'nl-NL',
                    indexAxis: 'y',
                    plugins: {
                        legend: {
                            align: 'start',
                        },
                        title: {
                            display: true,
                            text: this.$store.getters.getText('dashboardWidgetAmount'),
                        },
                        datalabels: {
                            anchor: 'start',
                            align: 'right',
                        }
                    },
                    maintainAspectRatio: false,
                    font: {
                        lineHeight: 1,
                    },
                    scales: {
                        x: {
                            grid: {
                                display: false
                            },
                        },
                        y: {
                            grid: {
                                display: false
                            },
                        },
                    }
                },
            };
        },
        computed: {
            facetsSet() {
                return this.data && this.data.facets && this.data.facets.length;
            },
            periodsSet() {
                return this.periods && this.periods.length;
            },
            dataSets() {
                if (!this.facetsSet || !this.periodsSet) return [];
                const facet = this.data.facets.find(f => f.alias === this.facetAlias);
                if (!facet || !facet.options || !facet.options.length) return [];

                const datasets = [];

                const inflowData = this.periods.filter(p => p).map(period => {
                    return typeof period === 'number' ?
                        facet.options
                            .filter(o => o.valueAlias.includes(period))
                            .reduce((sum, o) => sum + o.filteredAmount, 0):
                        facet.options
                            .filter(o => !o.valueAlias.includes(this.mostRecentYear) && !o.valueAlias.includes(this.secondRecentYear))
                            .reduce((sum, o) => sum + o.filteredAmount, 0)
                });
                const inflowBackgroundColor = this.$store.getters.getHighContrast ? '#833de6' : '#53b7d8';

                datasets.push({
                    label: this.$store.getters.getText('dashboardWidgetInflow'),
                    data: inflowData,
                    backgroundColor: inflowBackgroundColor,
                });

                const facetStatus = this.data.facets.find(f => f.alias === this.facetAliasStatus);
                if (!facetStatus || !facetStatus.options || !facetStatus.options.length) return datasets;
                const statuses = facetStatus.options.map(o => o.valueAlias).sort((a, b) => a.localeCompare(b));

                statuses.forEach(status => {
                    const statusData = this.periods.filter(p => p).map(period => {
                        let count = 0;
                        if (typeof period === 'number') {
                            const option = facet.options
                                .filter(o => o.valueAlias.includes(status))
                                .find(o => o.valueAlias.includes(period));
                            count = option ? option.filteredAmount : 0;
                        }
                        else {
                            count = facet.options
                                .filter(o => o.valueAlias.includes(status))
                                .filter(o => !o.valueAlias.includes(this.mostRecentYear) && !o.valueAlias.includes(this.secondRecentYear))
                                .reduce((sum, o) => sum + o.filteredAmount, 0);
                        }
                        return count;
                    });

                    const statusBackgroundColor = status === this.currentLabel ?
                        (this.$store.getters.getHighContrast ? '#056764' : '#ffb03b') :
                        (this.$store.getters.getHighContrast ? '#0d9dda' : '#e16032');

                    datasets.push({
                        label: this.capitalize(status),
                        data: statusData,
                        backgroundColor: statusBackgroundColor,
                    });
                });

                return {
                    labels: this.periods,
                    datasets: datasets,
                };
            },
            computedOptions() {
                return {
                    ...this.options,
                    plugins: {
                        ...this.options.plugins,
                        datalabels: {
                            ...this.options.plugins.datalabels,
                            color: this.$store.getters.getHighContrast ? '#ffffff' : '#000000',
                        },
                    },
                };
            },
        },
    };
</script>

<template>
    <div class="dashboard-widget dashboard-widget--minheight">
        <Bar :data="dataSets" :options="computedOptions" />
    </div>
</template>
